import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslationService } from '@trimble-gcs/i18n';
import { PluralArgs } from '@trimble-gcs/i18n-types';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { filter, map, switchMap } from 'rxjs';
import { ConnectService } from '../../../connect/connect.service';
import { DialogComponent } from '../../../dialog/dialog.component';
import { CANCEL_BUTTON, DialogData } from '../../../dialog/dialog.model';
import { DialogService } from '../../../dialog/dialog.service';
import { LoadingService } from '../../../loading/loading.service';
import { ScandataRecycleService } from '../../../scandata/recycle/scandata-recycle.service';
import { ScandataModel } from '../../../scandata/scandata.models';
import { multiSelectedTranslations } from './multi-selected.translations';
import { OptionsComponent } from './options/options.component';
import { TaggingComponent } from './tagging/tagging.component';

@UntilDestroy()
@Component({
  selector: 'sd-multi-selected',
  imports: [
    CommonModule,
    ModusButtonModule,
    ModusIconModule,
    ModusTooltipModule,
    MatProgressBarModule,
    OptionsComponent,
    TaggingComponent,
  ],
  templateUrl: './multi-selected.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectedComponent implements OnDestroy {
  private readonly loadingService = inject(LoadingService);
  private readonly scandataRecycleService = inject(ScandataRecycleService);
  private readonly connectService = inject(ConnectService);
  private readonly dialogService = inject(DialogService);
  private readonly translationService = inject(TranslationService);

  readonly translations = multiSelectedTranslations();

  readonly scandataModels = input.required<ScandataModel[]>();
  readonly readonly = input<boolean>(false);

  readonly downloadClicked = output();
  readonly unselectClicked = output<ScandataModel>();
  readonly unselectAllClicked = output();

  readonly showProgressBar = toSignal(this.loadingService.isLoading$(this), {
    initialValue: false,
  });
  readonly disabled = computed(() => this.showProgressBar());

  readonly showTagging = signal(false);
  readonly showOptions = computed(() => !this.showTagging());

  private confirmDialogRef: MatDialogRef<DialogComponent> | null = null;
  private errorDialogRef: MatDialogRef<DialogComponent> | null = null;

  ngOnDestroy(): void {
    this.confirmDialogRef?.close();
    this.errorDialogRef?.close();
  }

  viewIn3d() {
    this.connectService.goTo3dExtension();
  }

  deleteScandata() {
    this.showDeleteConfirmation(this.scandataModels())
      .pipe(
        switchMap((scandataModels) => {
          return this.loadingService.loadFrom(
            this.scandataRecycleService.deleteScans(scandataModels),
            this,
          );
        }),
      )
      .subscribe(({ failed }) => {
        if (failed.length === 0) return;

        this.showDeleteErrors(failed);
      });
  }

  private showDeleteConfirmation(scandataModels: ScandataModel[]) {
    const dialogTitle = this.translationService.translate(
      this.translations.confirmDeleteDialog.title.text,
      {
        'Reality Capture': 'Reality Capture',
      },
    );

    const message = this.translationService.translate(
      this.translations.confirmDeleteDialog.message.text,
      { count: scandataModels.length },
    );

    const dialogData = new DialogData(
      dialogTitle,
      message,
      { text: this.translations.confirmDeleteDialog.delete.text, color: 'danger' },
      CANCEL_BUTTON,
    );

    this.confirmDialogRef = this.dialogService.showMessage(dialogData);

    return this.confirmDialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed ?? false),
      map(() => scandataModels),
    );
  }

  private showDeleteErrors(failedModels: ScandataModel[]) {
    const names = `\n${failedModels.map((model) => model.name).join('\n')}`;
    const pluralArgs: PluralArgs = { count: failedModels.length, names };
    const pluralMap = this.translations.deleteErrorDialog.message;
    const message = this.translationService.translatePlural(pluralArgs, pluralMap);

    const dialogData = new DialogData(this.translations.deleteErrorDialog.title.text, message, {
      text: this.translations.deleteErrorDialog.dismiss.text,
      color: 'danger',
    });

    this.errorDialogRef = this.dialogService.showMessage(dialogData);
  }
}
